import {
  BreakpointsEnum,
  CardCarousel,
  Toast,
  ToastType,
  isBreakpointDown,
  useBreakpoint,
  useIsAndroid,
  useIsDesktop,
} from '@eCommerce/frontend-legacy-shared-library-native';
import envData from 'env.json';
import {DateTime} from 'luxon';
import {CloseIcon, FormControl, ScrollView, Text, View} from 'native-base';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Animated, Platform, Pressable} from 'react-native';

import AddToCalendarButton from 'components/AddToCalendarButton';
import ContainedImage from 'components/ContainedImage';
import OcrmRewardList from 'components/OcrmRewardList';
import PropertiesDropdown from 'components/PropertiesDropdown';
import WebViewModal from 'components/WebViewModal';
import {useGetFlyoutWidth} from 'hooks/useGetFlyoutWidth';
import {
  OCRMReward,
  OFFER_TERMS_ERROR,
  OFFER_TITLE_CUT_OFF_CHARACTER,
  OFFER_TITLE_MAX_CHARACTER,
  Offer,
  OfferSource,
} from 'models/Offer';
import {
  OCRMOfferAcceptTypes,
  OCRM_ACCEPT_STATUS,
} from 'store/offers/offers.reducer';
import {Properties} from 'store/properties/properties.interface';

import HeartFavorite from '../OfferCard/HeartFavorite';
import OfferExpirationDate from '../OfferCard/OfferExpirationDate';
import OCRMButtons from './OCRMButtons';
import OfferText from './OfferDetailsFormattedText';
import OfferDetailsStatus from './OfferDetailsStatus';
import PDBButtons from './PDBButtons';

const {API_URL, USE_STORYBOOK} = envData;
function convertToCalendarDateFormat(originalDate: string): string {
  const dateObject = new Date(originalDate);
  const formattedDate = dateObject.toISOString();
  return formattedDate;
}

type OfferDetailsProps = {
  offer: Offer;
  properties: Properties;
  onFavoritePress: () => void;
  closeSlideFlyout: () => void;
  terms: string | null;
  pdbHowRedeemText: string | undefined;
  ocrmPostAcceptMessage: string | undefined;
  ocrmErrorStateMessage: string | undefined;
  acceptOCRMOfferPress: (reward: OCRMReward) => void;
  ocrmAcceptFailureMessage: string | undefined;
  isOfferPrefLoading: boolean;
  ocrmAcceptState: OCRMOfferAcceptTypes;
  onNextOfferPress: () => void;
  onPreviousOfferPress: () => void;
  hasNextOffer: boolean;
  hasPreviousOffer: boolean;
  selectedGroupingName: string | undefined;
  isWebView?: boolean;
};

const OfferDetails: React.FC<OfferDetailsProps> = ({
  offer,
  properties,
  onFavoritePress,
  closeSlideFlyout,
  terms,
  pdbHowRedeemText,
  ocrmPostAcceptMessage,
  ocrmErrorStateMessage,
  acceptOCRMOfferPress,
  ocrmAcceptFailureMessage,
  isOfferPrefLoading,
  ocrmAcceptState,
  onNextOfferPress,
  onPreviousOfferPress,
  hasNextOffer,
  hasPreviousOffer,
  selectedGroupingName,
  isWebView = false,
}) => {
  const {end, title, description, pref, displayid, status, start} = offer;
  const scrollViewRef = useRef();
  const isFutureOffer = DateTime.fromISO(start).diffNow('days').days > 1;
  const formatDate = date => DateTime.fromISO(date).toFormat('MM.dd.yy');
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const SMALL_PHONE_BREAKPOINT = BreakpointsEnum.MICRO;
  const breakpoint = useBreakpoint();
  const isDesktop = useIsDesktop();
  const isWeb = Platform.OS === 'web';
  const isSmallPhone = isBreakpointDown(breakpoint, SMALL_PHONE_BREAKPOINT);
  const [isTermsVisible, setIsTermsVisible] = useState(false);
  const [isDownloadVisible, setIsDownloadVisible] = useState(false);
  const isOcrm = offer.source === OfferSource.ocrm;
  const isSingleRewardOcrm = isOcrm && offer.ocrm.rewards.length === 1;
  const isMultiRewardOcrm = isOcrm && offer.ocrm.rewards.length > 1;
  const firstReward = isOcrm ? offer.ocrm.rewards[0] : undefined;
  const [selectedReward, setSelectedReward] = useState<OCRMReward | undefined>(
    undefined
  );
  const offerDeeplink = `${API_URL}/rewards/offers?offerid=${offer.ruleId}`;

  const imageUrl = isOcrm
    ? offer.ocrm.rewards[0].links.link.find(link => link.rel === 'image')?.href
    : undefined;
  const propertyNames = useMemo(
    () => properties.map(property => property.propertyName),
    [properties]
  );

  const [selectedPropertyId, setSelectedPropertyId] = useState<string>(
    properties?.[0]?.id
  );
  const isAndroid = useIsAndroid();
  const flyoutWidth = useGetFlyoutWidth();
  const fadeAnim = useRef(new Animated.Value(0)).current;

  const renderFullPropertyList = (propertyNameList: string[]) => {
    if (propertyNameList.length === 1) {
      return (
        <View
          testID="offer-property"
          maxHeight="28px"
          mb="12px"
          mr={isSmallPhone ? '24px' : '48px'}>
          <Text
            color="tuna"
            fontSize="14px"
            fontFamily="body"
            lineHeight="14px">
            {propertyNameList[0]}
          </Text>
        </View>
      );
    }

    return propertyNameList.map((name: string) => (
      <View
        testID="offer-property"
        key={name}
        maxHeight="28px"
        width={isDesktop ? '185px' : '140px'}
        mb="12px"
        mr={isSmallPhone ? '24px' : '48px'}>
        <Text color="tuna" fontSize="14px" fontFamily="body" lineHeight="14px">
          {name}
        </Text>
      </View>
    ));
  };

  useEffect(() => {
    setSelectedPropertyId(properties?.[0]?.id);
  }, [properties]);

  useEffect(() => {
    if (ocrmAcceptState.status === OCRM_ACCEPT_STATUS.FAILED) {
      //@ts-expect-error scrollTo does not exist on type never
      scrollViewRef.current?.scrollTo({y: 0, animated: true});
    } else if (ocrmAcceptState.status === OCRM_ACCEPT_STATUS.SUCCESS) {
      //@ts-expect-error scrollToEnd does not exist on type never
      scrollViewRef.current?.scrollToEnd({animated: true});
    }
  }, [ocrmAcceptState.status]);

  const onAcceptOCRMOfferPress = () => {
    const nature = selectedReward?.nature;
    if (nature === 'CMS_CASH' || nature === 'FREE_CASH') {
      return setIsDownloadVisible(true);
    }
    return selectedReward && acceptOCRMOfferPress(selectedReward);
  };

  // Escape to close behavior
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const keyDownHandler = event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeSlideFlyout();
        }
      };

      document.addEventListener('keydown', keyDownHandler);

      // clean up event listener
      return () => {
        document.removeEventListener('keydown', keyDownHandler);
      };
    }
  });

  useEffect(() => {
    fadeAnim.setValue(0);
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 500,
      useNativeDriver: true,
    }).start();
  }, [fadeAnim, offer]);

  useEffect(() => {
    /**
     * This needs to be set in a useEffect so when user iterates
     * through offers using the arrows, the state updates accordingly
     */
    if (isOcrm) {
      setSelectedReward(firstReward);
    }
  }, [firstReward, isOcrm]);

  const [bookingUrl, setBookingUrl] = useState<string | undefined>(undefined);

  const ButtonsContainer = (
    <View
      pt={isDesktop ? undefined : 4}
      flexDirection={isDesktop ? 'row' : 'column'}
      width="100%"
      shadow={isDesktop ? undefined : 5}
      backgroundColor="white"
      justifyContent="space-between">
      <View
        minH="32px"
        width="100%"
        maxWidth={isDesktop ? flyoutWidth / 2 : undefined}>
        <CardCarousel
          onPrevPress={onPreviousOfferPress}
          onNextPress={onNextOfferPress}
          prevDisabled={!hasPreviousOffer}
          nextDisabled={!hasNextOffer}
          label={selectedGroupingName || 'OFFERS'}
        />
      </View>
      {offer.source === OfferSource.ocrm ? (
        isDownloadVisible || ocrmAcceptState.loading ? null : (
          <OCRMButtons
            offer={offer}
            closeSlideFlyout={closeSlideFlyout}
            acceptOCRMOfferPress={onAcceptOCRMOfferPress}
            isAcceptError={
              !!ocrmAcceptFailureMessage || !!ocrmErrorStateMessage
            }
          />
        )
      ) : (
        <PDBButtons
          offer={offer}
          closeSlideFlyout={closeSlideFlyout}
          selectedPropertyID={selectedPropertyId}
          setBookingUrl={(url: string) => setBookingUrl(url)}
        />
      )}
    </View>
  );

  const Details = (
    <Animated.View style={{height: '100%', flex: 1, opacity: fadeAnim}}>
      <View>
        {isDesktop ? (
          <View
            flexDirection="row"
            justifyContent="space-between"
            alignItems="baseline">
            <HeartFavorite
              pref={pref}
              onFavoritePress={onFavoritePress}
              isLarge={true}
              isOfferLoading={isOfferPrefLoading}
            />
            <Pressable onPress={closeSlideFlyout}>
              <CloseIcon size="24px" color="tuna" />
            </Pressable>
          </View>
        ) : (
          <View>
            <View alignSelf="flex-end" marginBottom={6}>
              <Pressable
                testID="offer-details-close-button"
                onPress={closeSlideFlyout}>
                <CloseIcon size="17px" color="tuna" />
              </Pressable>
            </View>
            <HeartFavorite
              pref={pref}
              onFavoritePress={onFavoritePress}
              isLarge={true}
              isOfferLoading={isOfferPrefLoading}
            />
          </View>
        )}
        <Text
          testID="offer-title"
          fontSize={isDesktop ? '36px' : '22px'}
          fontFamily="heading"
          fontWeight={isDesktop ? 'extrabold' : 'bold'}
          marginTop={isDesktop ? 7 : 5}
          marginBottom={5}>
          {title?.length <= OFFER_TITLE_MAX_CHARACTER
            ? title.toUpperCase()
            : `${title
                ?.substring(0, OFFER_TITLE_CUT_OFF_CHARACTER)
                .toUpperCase()}...`}
        </Text>
        <View
          alignItems={isDesktop ? 'center' : 'flex-start'}
          flexDirection={isDesktop ? 'row' : 'column'}
          pb={isDesktop ? 5 : 0}
          mb={isDesktop ? 5 : 6}
          borderBottomWidth={isDesktop ? 1 : 0}
          borderColor="#E2E4E6">
          <View
            justifyContent="space-between"
            flexDirection="row"
            width="100%"
            flex={['ios', 'android'].includes(Platform.OS) ? null : 1}>
            {isFutureOffer ? (
              <Text
                fontSize={'14px'}
                color="info"
                textAlign="center"
                fontWeight={700}>
                {`Valid: ${formatDate(start)} - ${formatDate(end)}`}
              </Text>
            ) : (
              <OfferExpirationDate startDate={start} endDate={end} size={14} />
            )}
            {displayid ? (
              <Text
                testID="offer-display-id"
                color="paleSky"
                fontSize="14px"
                fontWeight="bold">
                Offer {displayid}
              </Text>
            ) : null}
          </View>
        </View>
        {!isWebView && (
          <View
            alignItems={isDesktop ? 'flex-end' : 'center'}
            zIndex={200}
            pb={isDesktop ? 5 : 0}
            mb={isDesktop ? 5 : 6}>
            <AddToCalendarButton
              title={title}
              description={offerDeeplink + '\n\n' + (description || '')}
              startDate={convertToCalendarDateFormat(start)}
              endDate={convertToCalendarDateFormat(end)}
              offerId={offer.id}
            />
          </View>
        )}
        {ocrmAcceptFailureMessage || ocrmErrorStateMessage ? (
          <View mb={'20px'}>
            <Toast
              isDismissible={false}
              toast={{
                type: ToastType.DANGER,
                messageContent: ocrmErrorStateMessage
                  ? ocrmErrorStateMessage
                  : ocrmAcceptFailureMessage ?? '',
              }}
            />
          </View>
        ) : null}
        {isSingleRewardOcrm && imageUrl && (
          <View
            onLayout={({
              nativeEvent: {
                layout: {width},
              },
            }) => setContainerWidth(width)}
            mb={isDesktop ? 7 : 6}>
            <ContainedImage
              imageUrl={imageUrl}
              maxWidth={isDesktop ? containerWidth * 0.66 : 550}
            />
          </View>
        )}
        <OfferText
          fontSize={isDesktop ? '18px' : '14px'}
          lineHeight={isDesktop ? '36px' : '20px'}
          text={
            isSingleRewardOcrm ? offer.ocrm.rewards[0].description : description
          }
          mb={6}
        />
      </View>

      {isMultiRewardOcrm ? (
        <>
          <OcrmRewardList
            selectedReward={selectedReward}
            rewards={offer.ocrm.rewards}
            onSelected={reward => {
              setSelectedReward(reward);
              setIsDownloadVisible(false);
            }}
            isDownloadVisible={isDownloadVisible}
            setIsDownloadVisible={setIsDownloadVisible}
            onDownloadPress={() =>
              selectedReward && acceptOCRMOfferPress(selectedReward)
            }
          />
          <View height={4} />
        </>
      ) : (
        <View>
          {propertyNames.length ? (
            <>
              <Text
                fontSize={4}
                fontFamily="heading"
                fontWeight="bold"
                color="tuna"
                mb={4}>
                AVAILABLE HOTELS & RESORTS
              </Text>
              <View flexDir="row" flexWrap="wrap" mb="20px">
                {renderFullPropertyList(propertyNames)}
              </View>
            </>
          ) : null}
          {status.toLowerCase() === 'bookable' && propertyNames.length > 1 && (
            <View marginBottom={6}>
              <FormControl.Label>
                <Text fontWeight="semibold">HOTELS & RESORTS</Text>
              </FormControl.Label>
              <PropertiesDropdown
                isFullWidth={!isDesktop}
                properties={properties}
                selectedValue={selectedPropertyId}
                /** Should this have a better placeholder/aria-label? */
                placeholder="Select a hotel or resort to redeem this offer"
                onValueChange={setSelectedPropertyId}
              />
            </View>
          )}
          {isMultiRewardOcrm ? null : (
            <OfferDetailsStatus
              offer={offer}
              postAcceptMessage={ocrmPostAcceptMessage}
              isDownloadVisible={isDownloadVisible}
              setIsDownloadVisible={setIsDownloadVisible}
              onDownloadPress={() =>
                selectedReward && acceptOCRMOfferPress(selectedReward)
              }
            />
          )}
          {pdbHowRedeemText ? (
            <>
              <Text mb={4} fontWeight="bold" fontSize={4}>
                HOW TO REDEEM:
              </Text>
              <Text fontSize={3} lineHeight="24px" mb={isDesktop ? 6 : 5}>
                {pdbHowRedeemText}
              </Text>
            </>
          ) : null}
          <Pressable
            testID="terms-button"
            onPress={() => setIsTermsVisible(prevState => !prevState)}>
            <Text mb={isTermsVisible ? 4 : 6} underline fontWeight="semibold">
              Terms & Conditions
            </Text>
          </Pressable>
          {isTermsVisible ? (
            terms ? (
              <View mb={6}>
                {isOcrm ? (
                  <OfferText lineHeight="20px" text={terms} />
                ) : (
                  <Text lineHeight="20px">{terms}</Text>
                )}
              </View>
            ) : (
              <Toast
                toast={{
                  type: ToastType.DANGER,
                  messageContent: OFFER_TERMS_ERROR,
                  hide: false,
                }}
                autoHide={false}
                onRemove={() => setIsTermsVisible(prevState => !prevState)}
              />
            )
          ) : null}
        </View>
      )}
    </Animated.View>
  );

  const shouldScrollToBottom = isTermsVisible || isDownloadVisible;

  return (
    <>
      <FormControl testID="offer-details" flex={1}>
        {isDesktop ? (
          <ScrollView
            contentContainerStyle={{
              minHeight: '100%',
              padding: 48,
            }}
            flex={1}
            ref={scrollViewRef}
            onContentSizeChange={() => {
              if (shouldScrollToBottom) {
                //@ts-expect-error scrollToEnd does not exist on type never
                scrollViewRef.current?.scrollToEnd({animated: true});
              }
            }}>
            {Details}
            {ButtonsContainer}
          </ScrollView>
        ) : (
          <>
            <ScrollView
              contentContainerStyle={{flexGrow: 1, padding: 16}}
              ref={scrollViewRef}
              onContentSizeChange={() => {
                if (shouldScrollToBottom) {
                  //@ts-expect-error scrollToEnd does not exist on type never
                  scrollViewRef.current?.scrollToEnd({
                    animated: true,
                  });
                }
              }}
              showsVerticalScrollIndicator={false}>
              {Details}
            </ScrollView>
            {isAndroid && (
              <>
                <View height="1px" backgroundColor="ghostWhite" />
                <View height="2px" backgroundColor="athens" />
              </>
            )}
            {ButtonsContainer}
          </>
        )}
      </FormControl>
      {Platform.OS !== 'web' && (
        <WebViewModal
          showModal={!!bookingUrl}
          toggleModal={() => setBookingUrl(undefined)}
          uri={bookingUrl || ''}
          hasCloseButton={true}
          startInLoadingState={true}
        />
      )}
    </>
  );
};

export default OfferDetails;
